import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import KPICard from './components/KPICard';
import ProductionStatusCard from './components/ProductionStatusCard';
import RecentTransactionsTable from './components/RecentTransactionsTable';
import InventoryAlertsPanel from './components/InventoryAlertsPanel';
import ProductionChart from './components/ProductionChart';
import SalesChart from './components/SalesChart';
import QuickActionsPanel from './components/QuickActionsPanel';
import SubscriptionBanner from './components/SubscriptionBanner';

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);


  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Mock data for KPI cards
  const kpiData = [
    {
      title: "Total Inventory Pieces",
      value: "12,847",
      change: "+5.2%",
      changeType: "increase",
      icon: "Package",
      color: "primary"
    },
    {
      title: "Pending Orders",
      value: "23",
      change: "-2.1%",
      changeType: "decrease",
      icon: "ShoppingCart",
      color: "warning"
    },
    {
      title: "Active Production Jobs",
      value: "8",
      change: "+12.5%",
      changeType: "increase",
      icon: "Factory",
      color: "success"
    },
    {
      title: "Monthly Revenue",
      value: "₹2,45,680",
      change: "+18.3%",
      changeType: "increase",
      icon: "TrendingUp",
      color: "success"
    }
  ];

  // Mock data for production workflow
  const productionWorkflow = {
    title: "Production Workflow Status",
    totalJobs: 8,
    stages: [
      {
        name: "Digital Printing",
        operator: "Ramesh Kumar",
        count: 3,
        status: "In Progress"
      },
      {
        name: "Cutting Master",
        operator: "Suresh Patel",
        count: 2,
        status: "Queue"
      },
      {
        name: "Stitching Work",
        operator: "Meera Devi",
        count: 3,
        status: "In Progress"
      }
    ]
  };

  // Mock data for recent transactions
  const recentTransactions = [
    {
      id: "TXN-2025-001",
      type: "Sale",
      party: "AJIO Fashion",
      contact: "orders@ajio.com",
      amount: 45680,
      status: "Completed",
      date: "26/07/2025",
      time: "09:30 AM"
    },
    {
      id: "TXN-2025-002",
      type: "Purchase",
      party: "Cotton Suppliers Ltd",
      contact: "sales@cottonsuppliers.com",
      amount: 23400,
      status: "Processing",
      date: "25/07/2025",
      time: "02:15 PM"
    },
    {
      id: "TXN-2025-003",
      type: "Production",
      party: "Internal Production",
      contact: "production@nijanand.com",
      amount: 12300,
      status: "Pending",
      date: "25/07/2025",
      time: "11:45 AM"
    },
    {
      id: "TXN-2025-004",
      type: "Return",
      party: "MYNTRA",
      contact: "returns@myntra.com",
      amount: 8900,
      status: "Completed",
      date: "24/07/2025",
      time: "04:20 PM"
    }
  ];

  // Mock data for inventory alerts
  const inventoryAlerts = [
    {
      type: "Low Stock",
      item: "Cotton Fabric - White",
      message: "Stock running low, only 45 meters remaining",
      currentStock: "45 meters",
      minStock: "100 meters",
      priority: "High",
      time: "2 hours ago"
    },
    {
      type: "Out of Stock",
      item: "Polyester Thread - Black",
      message: "Completely out of stock, production may be affected",
      currentStock: "0 pieces",
      minStock: "50 pieces",
      priority: "High",
      time: "4 hours ago"
    },
    {
      type: "Quality Issue",
      item: "Printed Design - Floral Pattern",
      message: "Quality check failed for batch #FP-2025-15",
      currentStock: "200 pieces",
      minStock: "150 pieces",
      priority: "Medium",
      time: "6 hours ago"
    }
  ];

  // Mock data for production chart
  const productionChartData = [
    { name: 'Mon', printed: 120, cut: 100, stitched: 80 },
    { name: 'Tue', printed: 150, cut: 120, stitched: 95 },
    { name: 'Wed', printed: 180, cut: 150, stitched: 110 },
    { name: 'Thu', printed: 160, cut: 140, stitched: 125 },
    { name: 'Fri', printed: 200, cut: 180, stitched: 140 },
    { name: 'Sat', printed: 170, cut: 160, stitched: 130 },
    { name: 'Sun', printed: 140, cut: 130, stitched: 115 }
  ];

  // Mock data for sales chart
  const salesChartData = [
    { name: 'Floral Prints', value: 85000, pieces: 450 },
    { name: 'Geometric Patterns', value: 65000, pieces: 320 },
    { name: 'Abstract Designs', value: 45000, pieces: 280 },
    { name: 'Traditional Motifs', value: 35000, pieces: 180 },
    { name: 'Modern Graphics', value: 25000, pieces: 150 }
  ];

  // Mock subscription data
  const subscriptionData = {
    status: 'expiring', // active, expiring, expired, trial
    daysLeft: 5,
    plan: 'Pro Plan'
  };

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'new-order':
        window.location.href = '/invoice-challan-management';
        break;
      case 'add-inventory':
        window.location.href = '/inventory-management';
        break;
      case 'generate-report': alert('Report generation feature coming soon!');
        break;
      case 'production-schedule':
        window.location.href = '/production-workflow';
        break;
      default:
        console.log('Unknown action:', actionId);
    }
  };

  return (
    <div className="relative min-h-screen bg-background overflow-x-hidden">
      <Header />
      <Sidebar onCollapseChange={setIsSidebarCollapsed} />

      <main
  className={`
        transition-all duration-300
        pt-16
        ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        min-h-[calc(100vh-4rem)]
        px-4 sm:px-6
      `}
>
        <div className="flex-1 flex flex-col">
          <Breadcrumb />

          {/* Subscription Banner */}
          <SubscriptionBanner subscription={subscriptionData} />

          {/* Dashboard Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between flex-wrap gap-4 mb-8">
            <div className="min-w-0 flex-1">
              <h1 className="text-3xl font-bold text-foreground">Dashboard Overview</h1>
              <p className="text-muted-foreground">
                Welcome back, Rajesh! Here's what's happening with your textile business today.
              </p>
            </div>

            <p className="text-sm text-muted-foreground mt-1 whitespace-nowrap">
              Last updated: 03/08/2025, 11:09 am
            </p>
          </div>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {kpiData.map((kpi, index) => (
              <KPICard
                key={index}
                title={kpi.title}
                value={kpi.value}
                change={kpi.change}
                changeType={kpi.changeType}
                icon={kpi.icon}
                color={kpi.color}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8 items-start">
            {/* Production Status */}
            <div className="xl:col-span-1 h-full">
              <ProductionStatusCard
                title={productionWorkflow.title}
                stages={productionWorkflow.stages}
                totalJobs={productionWorkflow.totalJobs}
              />
            </div>

            {/* Recent Transactions */}
            <div className="xl:col-span-2 h-full">
              <RecentTransactionsTable transactions={recentTransactions} />
            </div>
          </div>

          {/* Secondary Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            {/* Inventory Alerts */}
            <div className="xl:col-span-1">
              <InventoryAlertsPanel alerts={inventoryAlerts} />
            </div>

            {/* Quick Actions */}
            <div className="xl:col-span-2">
              <QuickActionsPanel onAction={handleQuickAction} />
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
            {/* Production Chart */}
            <ProductionChart
              data={productionChartData}
              title="Daily Production Output"
            />

            {/* Sales Chart */}
            <SalesChart
              data={salesChartData}
              title="Design-wise Sales Performance"
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;